import Axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'

import { env } from '@/config/env-config'
import { getAuthHeader, useAuthStore } from '@/shared/auth/stores/authStore'

import { isJwtExpired } from './jwt'
import { normalizeSeverity } from './severity-normalize'
import { toast } from './toast'

export interface ApiResponse<T = unknown> {
  status: string
  message: string
  data: T
}

declare module 'axios' {
  interface AxiosRequestConfig {
    showToast?: boolean
    successMessage?: string
    suppressErrorToast?: boolean
  }
}

export const apiClient = Axios.create({
  baseURL: env.API_URL,
})

// GLOBAL STATE for 401/403 singleton handling
let forceLogoutSignal: AbortSignal | null = null

// A 401/403 only means "session expired" when there IS a session and its
// local token is dead (missing/expired), or the 403 is a gateway-level denial
// (API Gateway's own body, no standard failure envelope). Application 401/403s
// with a live token stay in-app as ordinary errors.
// See docs/FE_HANDOFF_session_expired_403.md in the backend repo.
const isSessionExpiry = (status: number | undefined, body: unknown): boolean => {
  if (status !== 401 && status !== 403) {
    return false
  }
  const { accessToken, isAuthenticated } = useAuthStore.getState()
  if (!isAuthenticated) {
    return false
  }
  const isTokenDead = !accessToken || isJwtExpired(accessToken)
  const isGatewayDenial =
    status === 403 && (body as { status?: unknown } | null | undefined)?.status !== 'failure'
  return isTokenDead || isGatewayDenial
}

// 401/403s arriving while the force-logout dialog is pending are the doomed
// requests racing in behind it — don't stack error toasts on the dialog.
const isDoomedAuthError = (status: number | undefined): boolean =>
  (status === 401 || status === 403) && useAuthStore.getState().forceLogout

// Request interceptor: attach Bearer token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authHeader = getAuthHeader()
    if (authHeader && !config.headers.Authorization) {
      config.headers.Authorization = authHeader
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor: toast + force-logout on 401/403
apiClient.interceptors.response.use(
  (response) => {
    if (forceLogoutSignal) {
      forceLogoutSignal = null
    }

    const method = response.config.method?.toUpperCase()
    const showToast = response.config.showToast
    const successMessage = response.config.successMessage
    const apiResponse = response.data as ApiResponse

    const message = successMessage || apiResponse?.message
    const shouldShowToast = method === 'GET' ? showToast === true : showToast !== false

    if (shouldShowToast && message) {
      toast.success(message)
    }

    return normalizeSeverity(response.data.data)
  },
  (error) => {
    const status = error.response?.status
    const body: unknown = error.response?.data

    const isLoginEndpoint = error.config?.url?.includes('/auth/login')
    const suppressToast = error.config?.suppressErrorToast === true

    if (isSessionExpiry(status, body) && !forceLogoutSignal && !isLoginEndpoint) {
      forceLogoutSignal = AbortSignal.timeout(0)
      useAuthStore.getState().setForceLogout(true)
    } else if (!isLoginEndpoint && !suppressToast && !isDoomedAuthError(status)) {
      toast.error((body as { message?: string } | null | undefined)?.message ?? 'An error occurred')
    }

    return Promise.reject(error)
  },
)

// Typed API wrapper
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.get(url, config) as Promise<T>,
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post(url, data, config) as Promise<T>,
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put(url, data, config) as Promise<T>,
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.patch(url, data, config) as Promise<T>,
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete(url, config) as Promise<T>,
}
